import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '../supabaseClient'
import { Subject } from '../types'

// ── DB row shape ──────────────────────────────────────────────────────────────
export interface StandardNode {
  id: string
  parent_id: string | null
  case_uuid: string | null
  subject: Subject
  grade: string // 'K','1'..'8','HSA','HSF','HSG','HSN','HSS','6-8','9-10','11-12','Anchor'
  statement_type: string | null
  normalized_type: 'Standard' | 'Standard Grouping' | 'Other'
  code: string | null // CCSS code (leaf nodes + grouping nodes that have codes)
  label: string
  body: string | null // full standard text (leaf nodes); cluster heading text (cluster nodes)
  sort_order: number
  introduction: string | null // grade-level intro paragraph (grade nodes only)
  critical_areas: string[] | null // numbered critical area paragraphs (grade nodes only)
  version: string | null // import version tag
}

// ── Assembled tree node ───────────────────────────────────────────────────────
export interface TreeNode {
  node: StandardNode
  children: TreeNode[]
}

// ── Return shape for findStandard (backward-compatible with old TREE version) ─
export interface StandardMeta {
  std: { id: string; text: string }
  subject: Subject
  grade: string
  domain: string
  cluster?: string
}

// ── Grade display labels ──────────────────────────────────────────────────────
const HS_LABELS: Record<string, string> = {
  HSN: 'HS Number & Quantity',
  HSA: 'HS Algebra',
  HSF: 'HS Functions',
  HSG: 'HS Geometry',
  HSS: 'HS Statistics & Probability',
}

export function gradeLabel(g: string): string {
  if (HS_LABELS[g]) return HS_LABELS[g]
  if (g === 'K') return 'Kindergarten'
  if (g === 'Anchor') return 'Anchor Standards'
  if (g.includes('-')) return `Grades ${g}`
  return `Grade ${g}`
}

// ── Grade ordering ────────────────────────────────────────────────────────────
export function gradeOrder(g: string): number {
  if (g === 'Anchor') return -1
  if (g === 'K') return 0
  const n = Number(g)
  if (!isNaN(n)) return n
  // Band grades: "6-8" → 6, "9-10" → 9, "11-12" → 11
  const band = g.match(/^(\d+)-/)
  if (band) return Number(band[1])
  const HS: Record<string, number> = { HSN: 20, HSA: 21, HSF: 22, HSG: 23, HSS: 24 }
  return HS[g] ?? 99
}

// ── Context ───────────────────────────────────────────────────────────────────
interface StandardsContextValue {
  loading: boolean
  /** Sorted list of grades available for a subject. */
  getGrades: (subject: Subject) => string[]
  /** Returns top-level domain/strand TreeNodes for a subject+grade. */
  getDomainTree: (subject: Subject, grade: string) => TreeNode[]
  /** Look up a standard by its CCSS code. Returns null while loading or if not found. */
  findStandard: (code: string) => StandardMeta | null
  /** Look up any node by its UUID. */
  findNode: (nodeId: string) => StandardNode | null
  /** All leaf standards (actual standards, not groupings) for a subject+grade. */
  getLeafStandards: (subject: Subject, grade: string) => StandardNode[]
  /** All leaf standard nodes under a given node UUID (recursive). */
  getDescendantLeafs: (nodeId: string) => StandardNode[]
}

const StandardsContext = createContext<StandardsContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────
export function StandardsProvider({ children }: { children: ReactNode }) {
  const [nodes, setNodes] = useState<StandardNode[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAll = async () => {
      const all: StandardNode[] = []
      const PAGE = 1000
      let from = 0
      while (true) {
        const { data, error } = await supabase
          .from('standard_nodes')
          .select('*')
          .order('id') // stable UUID sort for reliable pagination
          .range(from, from + PAGE - 1)
        if (error) {
          console.error('StandardsProvider load error:', error.message)
          break
        }
        if (!data || data.length === 0) break
        all.push(...(data as StandardNode[]))
        if (data.length < PAGE) break
        from += PAGE
      }
      setNodes(all)
      setLoading(false)
    }
    fetchAll()
  }, [])

  // ── Lookup maps (rebuilt when nodes change) ──────────────────────────────
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const byCode = new Map(nodes.filter((n) => n.code).map((n) => [n.code!, n]))

  // ── getGrades ────────────────────────────────────────────────────────────
  const getGrades = (subject: Subject): string[] => {
    const grades = new Set<string>()
    nodes.forEach((n) => {
      if (n.subject === subject) grades.add(n.grade)
    })
    return Array.from(grades).sort((a, b) => gradeOrder(a) - gradeOrder(b))
  }

  // ── getDomainTree ─────────────────────────────────────────────────────────
  const getDomainTree = (subject: Subject, grade: string): TreeNode[] => {
    const subjectGradeNodes = nodes.filter((n) => n.subject === subject && n.grade === grade)
    const childrenOf = new Map<string | null, StandardNode[]>()
    for (const n of subjectGradeNodes) {
      const key = n.parent_id ?? null
      if (!childrenOf.has(key)) childrenOf.set(key, [])
      childrenOf.get(key)!.push(n)
    }
    const buildTree = (parentId: string | null): TreeNode[] =>
      (childrenOf.get(parentId) || [])
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((n) => ({ node: n, children: buildTree(n.id) }))

    return buildTree(null)
  }

  // ── findStandard ──────────────────────────────────────────────────────────
  const findStandard = (code: string): StandardMeta | null => {
    const node = byCode.get(code)
    if (!node || node.normalized_type !== 'Standard') return null

    // Walk up to find domain (top-level grouping) and cluster (second-level grouping)
    let current: StandardNode | undefined = node
    let domain = ''
    let cluster: string | undefined

    const ancestors: StandardNode[] = []
    while (current?.parent_id) {
      const parent = byId.get(current.parent_id)
      if (!parent) break
      ancestors.push(parent)
      current = parent
    }

    // ancestors[0] = immediate parent (cluster or strand)
    // ancestors[1] = grandparent (domain) if cluster exists
    if (ancestors.length >= 2) {
      domain = ancestors[ancestors.length - 1].label
      cluster = ancestors[0].label
    } else if (ancestors.length === 1) {
      domain = ancestors[0].label
    }

    return {
      std: { id: node.code!, text: node.body || '' },
      subject: node.subject,
      grade: node.grade,
      domain,
      cluster,
    }
  }

  // ── findNode ─────────────────────────────────────────────────────────────
  const findNode = (nodeId: string): StandardNode | null => byId.get(nodeId) ?? null

  // ── getLeafStandards ──────────────────────────────────────────────────────
  const getLeafStandards = (subject: Subject, grade: string): StandardNode[] =>
    nodes.filter(
      (n) =>
        n.subject === subject &&
        n.grade === grade &&
        n.normalized_type === 'Standard' &&
        n.statement_type !== 'Component',
    )

  // ── getDescendantLeafs ────────────────────────────────────────────────────
  const childrenIndex = new Map<string, StandardNode[]>()
  for (const n of nodes) {
    if (!n.parent_id) continue
    if (!childrenIndex.has(n.parent_id)) childrenIndex.set(n.parent_id, [])
    childrenIndex.get(n.parent_id)!.push(n)
  }

  const getDescendantLeafs = (nodeId: string): StandardNode[] => {
    const result: StandardNode[] = []
    const walk = (id: string) => {
      const children = childrenIndex.get(id) || []
      for (const child of children) {
        if (child.normalized_type === 'Standard') result.push(child)
        else walk(child.id)
      }
    }
    walk(nodeId)
    return result.sort((a, b) => a.sort_order - b.sort_order)
  }

  return (
    <StandardsContext.Provider
      value={{ loading, getGrades, getDomainTree, findStandard, findNode, getLeafStandards, getDescendantLeafs }}
    >
      {children}
    </StandardsContext.Provider>
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useStandards(): StandardsContextValue {
  const ctx = useContext(StandardsContext)
  if (!ctx) throw new Error('useStandards must be used inside StandardsProvider')
  return ctx
}
